<template>
  <div class="flex flex-col h-full text-white">
    <!-- Header -->
    <div
      class="flex justify-center items-center px-1 pb-3 mb-4 border-b border-gray-800">
      <h2 class="text-lg font-semibold">
        {{ headerText }}
      </h2>
    </div>

    <!-- Scrollable content -->
    <div class="overflow-y-auto flex-1">
      <!-- Success celebration -->
      <!-- <div class="flex flex-col items-center py-6 space-y-3">
        <div class="text-5xl animate-bounce">{{ successEmoji }}</div>
        <div class="text-3xl font-bold" :style="{ color: scoreColorValue }">
          {{ scoreOverall }}
        </div>
        <p class="text-sm text-gray-400">{{ subtitleText }}</p>
      </div> -->

      <!-- Share image section -->
      <div class="px-0 pb-4">
        <ShareImageDownloads
          v-if="review"
          :review="review"
          :title="title"
          :image-blobs="imageBlobs"
          :score-color="scoreColorValue"
          label="Share Image" />
      </div>

      <!-- Link sharing buttons -->
      <div class="px-0 pb-4">
        <ShareLinkButtons
          :link="reviewLink"
          :whatsapp-message="whatsappMessage" />
      </div>

      <!-- Add to list section -->
      <!-- <div class="px-4 pb-4">
        <AddToListSection :title="title" />
      </div> -->

      <!-- Send to friend section -->
      <div v-if="canUseSendTo" class="px-4 pb-4">
        <ShareToFriendButton :review="review" :title="title" />
      </div>
    </div>

    <!-- Footer with view button -->
    <div class="py-0 px-0 border-t border-gray-800 shrink-0">
      <button
        class="py-3 px-4 w-full text-sm font-semibold text-white rounded-lg transition-colors"
        :style="{ backgroundColor: scoreColorValue }"
        @click="handleViewReview">
        {{ viewButtonText }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useReviewColors } from '@/use/useReviewColors'
import { useFeatureFlags } from '@/use/useFeatureFlags'
import ShareImageDownloads from '@/components/Share/ShareImageDownloads.vue'
import ShareLinkButtons from '@/components/Share/ShareLinkButtons.vue'
import ShareToFriendButton from '@/components/Share/ShareToFriendButton.vue'
import AddToListSection from '@/components/Share/AddToListSection.vue'

const router = useRouter()
const { scoreHsl } = useReviewColors()
const { canUseSendTo } = useFeatureFlags()

const props = defineProps({
  review: {
    type: Object,
    required: true
  },
  title: {
    type: Object,
    required: true
  },
  mode: {
    type: String,
    default: 'created', // 'created' | 'updated' | 'view'
    validator: (value) => ['created', 'updated', 'view'].includes(value)
  },
  scoreColor: {
    type: String,
    default: null
  },
  imageBlobs: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'view-review'])

// Computed
const headerText = computed(() => {
  switch (props.mode) {
    case 'created':
      return 'Review Created!'
    case 'updated':
      return 'Review Updated!'
    default:
      return 'Share Review'
  }
})

const subtitleText = computed(() => {
  switch (props.mode) {
    case 'created':
      return 'Your review has been saved'
    case 'updated':
      return 'Your changes have been saved'
    default:
      return ''
  }
})

const viewButtonText = computed(() => {
  switch (props.mode) {
    case 'created':
    case 'updated':
      return 'View Review'
    default:
      return 'Done'
  }
})

const scoreOverall = computed(() => {
  if (!props.review) return '0.0'
  const overall =
    ((props.review.score_a || 0) +
      (props.review.score_b || 0) +
      (props.review.score_c || 0) +
      (props.review.score_d || 0) +
      (props.review.score_e || 0) +
      (props.review.score_f || 0)) /
    6
  return overall.toFixed(1)
})

const scoreColorValue = computed(() => {
  if (props.scoreColor) return props.scoreColor
  return scoreHsl(parseFloat(scoreOverall.value))
})

const successEmoji = computed(() => {
  const score = parseFloat(scoreOverall.value)
  if (score >= 9) return '🎉'
  if (score >= 7) return '🙂'
  if (score >= 5) return '😐'
  if (score >= 3) return '😕'
  return '😢'
})

// Computed link and message for sharing
const reviewLink = computed(() => {
  return props.review?.id
    ? `${window.location.origin}/reviews/${props.review.id}`
    : window.location.href
})

const whatsappMessage = computed(() => {
  const titleName = props.title?.title || props.title?.name || 'this'
  return `Check out my review of ${titleName}: ${reviewLink.value}`
})

// Actions
const handleClose = () => {
  emit('close')
}

const handleViewReview = () => {
  if (props.review?.id) {
    router.push({ name: 'WebReviewsSingle', params: { id: props.review.id } })
  }
  emit('close')
}
</script>
